import React from "react";
import { Activity, BookOpenCheck, CalendarCheck, IndianRupee } from "lucide-react";
import { ProgressBar, StatsCard, StatsGrid } from "../../ui";
import { formatCurrency, formatDate } from "../../../utils/format";
import { idleLabel } from "./StudentIdentityCard";

/**
 * A tile's icon carries colour only when its number needs attention — a row of
 * four tiles in four hues says nothing, and then the red one says nothing too.
 * The keys are studentRecord tones; the values are StatsCard's tone names.
 */
const ICON_TONE = { success: "emerald", warning: "amber", danger: "rose", neutral: "primary" };

/** The line under a figure, and the meter that draws the same number. */
const Measure = ({ children, value, tone }) => (
  <>
    <span className="tw-block">{children}</span>
    {value !== null && value !== undefined && (
      <ProgressBar value={value} color={tone} size="sm" className="tw-mt-2" />
    )}
  </>
);

/**
 * StudentVitals — the four numbers an admin reads first: is the student coming
 * to class, how far along is their batch, what do they owe, and are they
 * still using the app.
 *
 * Props:
 *   attendance  — studentRecord.attendanceStanding()
 *   stats       — the `/dashboard/student/:id` resource
 *   fees        — studentRecord.summarizeFees(), or null when fees are off
 *   installments— studentRecord.summarizeInstallments(), or null
 *   activity    — studentRecord.engagement()
 *   hasBatch    — whether the student has a primary batch at all
 */
const StudentVitals = ({ attendance, stats, fees, installments, activity, hasBatch }) => {
  const statsLoading = stats.loading && !stats.data;
  const course = stats.data?.courseCompletion;
  const overdue = installments?.overdueCount > 0;

  return (
    <StatsGrid cols={fees ? 4 : 3}>
      <StatsCard
        title="Attendance"
        icon={CalendarCheck}
        iconColor={ICON_TONE[attendance.tone]}
        loading={statsLoading}
        value={attendance.percent === null ? "—" : `${attendance.percent}%`}
        subtitle={attendance.held ? (
          <Measure value={attendance.percent} tone={attendance.tone === "neutral" ? "primary" : attendance.tone}>
            {attendance.present} of {attendance.held} classes
            {attendance.partial > 0 && ` · ${attendance.partial} partial`}
          </Measure>
        ) : (
          <Measure>{hasBatch ? "No classes held yet" : "Not in a batch yet"}</Measure>
        )}
      />

      <StatsCard
        title="Course progress"
        icon={BookOpenCheck}
        iconColor="primary"
        loading={statsLoading}
        value={course?.totalSessions ? `${course.percent}%` : "—"}
        subtitle={course?.totalSessions ? (
          <Measure value={course.percent} tone="primary">
            {course.completedSessions} of {course.totalSessions} sessions covered
          </Measure>
        ) : (
          <Measure>{hasBatch ? "No syllabus sessions yet" : "Not in a batch yet"}</Measure>
        )}
      />

      {fees && (
        <StatsCard
          title="Fee balance"
          icon={IndianRupee}
          iconColor={overdue ? "rose" : "primary"}
          value={fees.total > 0 ? formatCurrency(fees.pending) : "—"}
          subtitle={fees.total > 0 ? (
            <Measure value={fees.paidPct} tone="success">
              {formatCurrency(fees.paid)} of {formatCurrency(fees.total)} paid
              {overdue && (
                <span className="tw-block tw-font-medium tw-text-danger-fg">
                  {formatCurrency(installments.overdueAmount)} overdue
                </span>
              )}
              {!overdue && installments?.next && (
                <span className="tw-block">
                  Next {formatCurrency(installments.next.balance)} on {formatDate(installments.next.dueDate)}
                </span>
              )}
            </Measure>
          ) : (
            <Measure>No fees set</Measure>
          )}
        />
      )}

      <StatsCard
        title="Last active"
        icon={Activity}
        iconColor={ICON_TONE[activity?.tone || "neutral"]}
        value={activity?.lastActiveAt
          ? idleLabel(activity.idleDays).replace(/^./, (c) => c.toUpperCase())
          : "—"}
        subtitle={!activity ? (
          <Measure>Not available</Measure>
        ) : !activity.hasLogin ? (
          <Measure>Has not signed up yet</Measure>
        ) : (
          <Measure value={Math.round((activity.activeDays30 / 30) * 100)} tone={activity.tone === "warning" ? "warning" : "primary"}>
            {activity.activeDays30} of the last 30 days
          </Measure>
        )}
      />
    </StatsGrid>
  );
};

export default StudentVitals;
